// src/components/CheckoutDormSignoffModal.jsx
//
// Dorm-specific signoff modal. Includes the 15-item inventory checklist and
// a damage notes textarea. The Cloud Function (signoffCheckoutSection)
// accepts the inventory map and damage notes alongside the standard signoff
// payload when sectionId === 'dorm'.
//
// Modes: 'sign' | 'undo'.
// (No 'na' mode here — superadmins use the generic modal for that since
// it doesn't need the inventory UI.)

import React, { useState, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  DORM_INVENTORY_ITEMS,
  KA_COLORS,
} from '../utils/checkoutHelpers';

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 8,
    width: '100%', maxWidth: 620, maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  header: (mode) => ({
    background: mode === 'undo' ? '#777' : KA_COLORS.navy,
    color: '#fff',
    padding: '16px 20px', borderRadius: '8px 8px 0 0',
  }),
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  headerSubtitle: { margin: '4px 0 0', fontSize: 13, opacity: 0.85 },
  body: { padding: 20 },
  studentBlock: {
    background: KA_COLORS.lightGray, borderRadius: 6,
    padding: 12, marginBottom: 16,
  },
  studentName: { fontSize: 14, fontWeight: 600, color: KA_COLORS.navy, margin: 0 },
  studentMeta: { fontSize: 12, color: '#666', margin: '4px 0 0' },
  fieldLabel: {
    display: 'block', fontSize: 13, fontWeight: 600,
    color: '#333', marginBottom: 6,
  },
  inventoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '6px 16px',
    marginBottom: 12,
    background: KA_COLORS.lightGray,
    padding: 12,
    borderRadius: 4,
    border: `1px solid ${KA_COLORS.borderGray}`,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: '#333',
    cursor: 'pointer',
    padding: '4px 0',
  },
  checkbox: { width: 16, height: 16, cursor: 'pointer' },
  controlsRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 8,
    fontSize: 12,
  },
  linkBtn: {
    background: 'none', border: 'none',
    color: KA_COLORS.navy, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', textDecoration: 'underline',
    padding: 0, fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', padding: '8px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 4,
    fontSize: 13, minHeight: 60, fontFamily: 'inherit',
    resize: 'vertical', boxSizing: 'border-box',
  },
  damageHint: {
    fontSize: 11, color: '#999', margin: '4px 0 0', fontStyle: 'italic',
  },
  prevSignoff: {
    padding: 10, marginBottom: 12,
    background: '#E8F5E9', border: '1px solid #C8E6C9',
    borderRadius: 4, fontSize: 13, color: '#2E7D32',
  },
  errorBox: {
    marginTop: 12, padding: 10,
    background: '#FFEBEE', border: '1px solid #FFCDD2',
    borderRadius: 4, color: '#C62828', fontSize: 13,
  },
  footer: {
    padding: '14px 20px', borderTop: `1px solid ${KA_COLORS.borderGray}`,
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  },
  cancelBtn: {
    padding: '8px 16px', border: `1px solid ${KA_COLORS.borderGray}`,
    background: '#fff', borderRadius: 4, cursor: 'pointer',
    fontSize: 14, color: '#333',
  },
  confirmBtn: (disabled, mode) => ({
    padding: '8px 16px', border: 'none',
    background: disabled
      ? '#ccc'
      : mode === 'undo'
      ? '#777'
      : KA_COLORS.red,
    color: '#fff', borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 14, fontWeight: 600,
  }),
};

// -----------------------------------------------------------------------------

const buildEmptyInventory = () =>
  DORM_INVENTORY_ITEMS.reduce((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {});

const buildAllCheckedInventory = () =>
  DORM_INVENTORY_ITEMS.reduce((acc, item) => {
    acc[item.id] = true;
    return acc;
  }, {});

// -----------------------------------------------------------------------------

const CheckoutDormSignoffModal = ({
  record,
  mode = 'sign', // 'sign' | 'undo'
  onClose,
  onSuccess,
}) => {
  const dormSection = record?.sections?.dorm;

  // For sign mode: start with current inventory state if any, else empty
  const initialInventory = useMemo(() => {
    if (mode === 'undo') return dormSection?.inventory || buildEmptyInventory();
    return dormSection?.inventory && Object.keys(dormSection.inventory).length > 0
      ? dormSection.inventory
      : buildEmptyInventory();
  }, [dormSection, mode]);

  const [inventory, setInventory] = useState(initialInventory);
  const [damageNotes, setDamageNotes] = useState(dormSection?.damageNotes || '');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!record) return null;

  const checkedCount = Object.values(inventory).filter(Boolean).length;
  const totalItems = DORM_INVENTORY_ITEMS.length;

  const toggleItem = (itemId) => {
    setInventory((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  const checkAll = () => setInventory(buildAllCheckedInventory());
  const clearAll = () => setInventory(buildEmptyInventory());

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const functions = getFunctions();
      const signoffCheckoutSection = httpsCallable(
        functions,
        'signoffCheckoutSection',
      );
      await signoffCheckoutSection({
        recordId: record.id || `${record.schoolYear}_${record.studentId}`,
        sectionId: 'dorm',
        action: mode,
        notes: mode === 'sign' ? notes.trim() : '',
        reason: mode === 'undo' ? reason.trim() : '',
        // dorm-specific payload (only sent when signing)
        inventory: mode === 'sign' ? inventory : undefined,
        damageNotes: mode === 'sign' ? damageNotes.trim() : undefined,
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('signoffCheckoutSection (dorm) failed:', err);
      setError(err?.message || 'The signoff failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true">
        <div style={styles.header(mode)}>
          <h2 style={styles.headerTitle}>
            {mode === 'undo' ? 'Undo Dorm Signoff' : 'Dorm Faculty Signoff'}
          </h2>
          <p style={styles.headerSubtitle}>
            {mode === 'undo'
              ? 'Reverting the dorm signoff. Inventory state is preserved on the record but the signed status will return to pending.'
              : 'Confirm the room inventory and note any damage. A flat JD50 charge applies to any damage.'}
          </p>
        </div>

        <div style={styles.body}>
          <div style={styles.studentBlock}>
            <p style={styles.studentName}>{record.studentName || 'Unknown'}</p>
            <p style={styles.studentMeta}>
              {[record.grade, record.cohort, record.dorm && `Dorm: ${record.dorm}`,
                record.room && `Room: ${record.room}`,
                record.studentId && `SIS ${record.studentId}`]
                .filter(Boolean)
                .join('  •  ')}
            </p>
          </div>

          {mode === 'undo' && dormSection?.signedByName && (
            <div style={styles.prevSignoff}>
              Currently signed off by{' '}
              <strong>{dormSection.signedByName}</strong>
              {dormSection.notes && (
                <>
                  {' '}— <em>{dormSection.notes}</em>
                </>
              )}
            </div>
          )}

          {mode === 'sign' && (
            <>
              <label style={styles.fieldLabel}>
                Room inventory ({checkedCount} of {totalItems} checked)
              </label>
              <div style={styles.controlsRow}>
                <button type="button" style={styles.linkBtn} onClick={checkAll}>
                  Check all
                </button>
                <button type="button" style={styles.linkBtn} onClick={clearAll}>
                  Clear all
                </button>
              </div>

              <div style={styles.inventoryGrid}>
                {DORM_INVENTORY_ITEMS.map((item) => (
                  <label
                    key={item.id}
                    style={styles.checkboxRow}
                    htmlFor={`dorm-${item.id}`}
                  >
                    <input
                      id={`dorm-${item.id}`}
                      type="checkbox"
                      style={styles.checkbox}
                      checked={!!inventory[item.id]}
                      onChange={() => toggleItem(item.id)}
                      disabled={submitting}
                    />
                    {item.label}
                  </label>
                ))}
              </div>

              <label style={styles.fieldLabel} htmlFor="damage-notes">
                Damage notes (note any items missing or broken)
              </label>
              <textarea
                id="damage-notes"
                style={styles.textarea}
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
                placeholder="e.g., Side lamp broken, JD50 charge applied. Cupboard door scratched."
                disabled={submitting}
              />
              <p style={styles.damageHint}>
                Any damage will incur a flat JD50 charge.
              </p>

              <label
                style={{ ...styles.fieldLabel, marginTop: 16 }}
                htmlFor="dorm-notes"
              >
                Additional notes (optional)
              </label>
              <textarea
                id="dorm-notes"
                style={styles.textarea}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything else worth recording on the signoff."
                disabled={submitting}
              />
            </>
          )}

          {mode === 'undo' && (
            <>
              <label style={styles.fieldLabel} htmlFor="undo-reason">
                Reason for undo (optional)
              </label>
              <textarea
                id="undo-reason"
                style={styles.textarea}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Student returned to retrieve item; need to recheck room before final signoff."
                disabled={submitting}
              />
            </>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            style={styles.confirmBtn(submitting, mode)}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting
              ? 'Working…'
              : mode === 'undo'
              ? 'Confirm undo'
              : 'Sign off room'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutDormSignoffModal;